<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card class="challenge-creator">
      <!-- Header -->
      <div class="creator-header">
        <div class="header-content">
          <v-icon class="header-icon">mdi-trophy-award</v-icon>
          <div class="header-text">
            <h2>Create a new challenge</h2>
            <p>Publish a challenge for the week {{ currentWeek }}</p>
          </div>
        </div>
        <v-btn icon variant="text" @click="closeDialog" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: step === 1, completed: step > 1 }">
          <div class="step-number">1</div>
          <span>Image</span>
        </div>
        <div class="step-line" :class="{ completed: step > 1 }"></div>
        <div class="step" :class="{ active: step === 2, completed: step > 2 }">
          <div class="step-number">2</div>
          <span>Conditions</span>
        </div>
        <div class="step-line" :class="{ completed: step > 2 }"></div>
        <div class="step" :class="{ active: step === 3, completed: step > 3 }">
          <div class="step-number">3</div>
          <span>Reward</span>
        </div>
      </div>

      <!-- Content -->
      <div class="creator-content">
        <!-- Step 1: Image Upload -->
        <div v-if="step === 1" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-image-outline</v-icon>
            <h3>Challenge Image</h3>
          </div>
          
          <div class="image-upload-section">
            <div v-if="!challengeForm.imageUrl" class="upload-area" @click="$refs.fileInput.click()">
              <v-icon size="48" class="upload-icon">mdi-cloud-upload-outline</v-icon>
              <h4>Click to upload image</h4>
              <p>Supported formats: JPG, PNG (Max 5MB)</p>
              <v-btn variant="outlined" :loading="imageUploading" class="upload-btn">
                Choose File
              </v-btn>
            </div>
            
            <div v-else class="image-preview">
              <img :src="challengeForm.imageUrl" alt="Challenge Image" />
              <div class="image-actions">
                <v-btn variant="outlined" @click="$refs.fileInput.click()" :loading="imageUploading">
                  <v-icon left>mdi-image-edit</v-icon>
                  Change Image
                </v-btn>
              </div>
            </div>
            
            <input 
              ref="fileInput" 
              type="file" 
              accept="image/*" 
              @change="handleFileSelect" 
              style="display: none"
            />
          </div>

          <div class="form-group">
            <label class="field-label">Image Credit</label>
            <v-text-field
              v-model="challengeForm.imageBy"
              placeholder="e.g., Photographer Name"
              variant="outlined"
              density="compact"
              class="creator-input"
            ></v-text-field>
          </div>
        </div>

        <!-- Step 2: Conditions -->
        <div v-if="step === 2" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-cog-outline</v-icon>
            <h3>Challenge Conditions</h3>
          </div>

          <!-- Race Selection -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-flag-checkered</v-icon>
              <h4>Event</h4>
              <span class="required-badge">Required</span>
            </div>
            <v-select
              v-model="challengeForm.raceID"
              :options="tracks"
              label="name"
              :reduce="track => track.id"
              placeholder="Select a race track"
              :loading="tracksLoading"
              class="creator-select"
            ></v-select>
          </div>

          <!-- Vehicle Restrictions -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-car</v-icon>
              <h4>Vehicle Restrictions</h4>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label">Car</label>
                <v-select
                  v-model="challengeForm.car"
                  :options="cars"
                  label="name"
                  :reduce="car => car.id"
                  placeholder="Any car allowed"
                  clearable
                  class="creator-select"
                ></v-select>
              </div>
              
              <div class="form-field">
                <label class="field-label">Class</label>
                <v-select
                  v-model="challengeForm.carClass"
                  :options="classOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Any class allowed"
                  clearable
                  class="creator-select"
                ></v-select>
              </div>
            </div>
          </div>

          <!-- Performance Conditions -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-speedometer</v-icon>
              <h4>Requirements</h4>
              <span class="required-badge">At least 1 required</span>
            </div>

            <div class="performance-grid">
              <!-- Cops Rammed -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-police-badge</v-icon>
                  <span>Cops Rammed</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCopsRammed"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.copsRammed"
                    type="number"
                    placeholder="Value"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Cops Disabled -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-police-badge-outline</v-icon>
                  <span>Cops Disabled</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCopsDisabled"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.copsDisabled"
                    type="number"
                    placeholder="Value"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Top Speed -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-speedometer</v-icon>
                  <span>Top Speed (km/h)</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorTopSpeed"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.topSpeed"
                    type="number"
                    placeholder="200"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Cost to State -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-cash</v-icon>
                  <span>Cost to State</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCostToState"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.costToState"
                    type="number"
                    placeholder="1500000"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Duration -->
              <div class="performance-item duration-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-timer-outline</v-icon>
                  <span>Race Duration</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorDuration"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <div class="duration-inputs">
                    <v-text-field
                      v-model="challengeForm.durationMin"
                      type="number"
                      placeholder="Min"
                      variant="outlined"
                      density="compact"
                      class="duration-input"
                    ></v-text-field>
                    <span class="duration-separator">:</span>
                    <v-text-field
                      v-model="challengeForm.durationSec"
                      type="number"
                      placeholder="Sec"
                      variant="outlined"
                      density="compact"
                      class="duration-input"
                    ></v-text-field>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Game Settings -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-lightning-bolt</v-icon>
              <h4>Restrictions</h4>
            </div>
            
            <div class="form-field">
              <label class="field-label">Powerups</label>
              <v-select
                v-model="challengeForm.powerups"
                :options="powerupOptions"
                label="title"
                :reduce="option => option.value"
                placeholder="No restriction"
                clearable
                class="creator-select"
              ></v-select>
            </div>
          </div>
        </div>

        <!-- Step 3: Reward -->
        <div v-if="step === 3" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-gift-outline</v-icon>
            <h3>Challenge Reward</h3>
          </div>

          <!-- Difficulty Selection -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-target</v-icon>
              <h4>Challenge Difficulty</h4>
              <span class="required-badge">Required</span>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label required-label">Difficulty Level</label>
                <v-select
                  v-model="challengeForm.difficulty"
                  :options="difficultyOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Select difficulty level"
                  class="creator-select difficulty-select"
                ></v-select>
              </div>
            </div>
            
            <!-- Difficulty Preview -->
            <div v-if="challengeForm.difficulty !== null && challengeForm.difficulty !== undefined" class="difficulty-preview">
              <div class="difficulty-badge" :style="{ backgroundColor: difficultyOptions.find(d => d.value === challengeForm.difficulty)?.color }">
                <v-icon class="difficulty-icon">mdi-target</v-icon>
                {{ difficultyOptions.find(d => d.value === challengeForm.difficulty)?.title }}
              </div>
            </div>
          </div>

          <!-- Reward Configuration -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-gift</v-icon>
              <h4>Reward Configuration</h4>
              <span class="required-badge">Required</span>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label required-label">Reward Amount</label>
                <v-text-field
                  v-model="challengeForm.reward"
                  type="number"
                  placeholder="1000000"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="creator-input"
                ></v-text-field>
              </div>
              
              <div class="form-field">
                <label class="field-label required-label">Reward Type</label>
                <v-select
                  v-model="challengeForm.rewardType"
                  :options="rewardTypeOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Select reward type"
                  class="creator-select reward-type-select"
                ></v-select>
              </div>
            </div>
          </div>

          <!-- Reward Preview -->
          <div v-if="challengeForm.reward && challengeForm.rewardType" class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-eye</v-icon>
              <h4>Reward Preview</h4>
            </div>
            
            <div class="reward-preview">
              <div class="reward-card">
                <v-icon class="reward-icon">mdi-gift</v-icon>
                <div class="reward-details">
                  <h4>Challenge Reward</h4>
                  <p class="reward-amount">
                    {{ formatNumber(challengeForm.reward) }} 
                    {{ challengeForm.rewardType === '0' ? '$' : 'SB' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="creator-actions">
        <div class="actions-left">
          <v-btn 
            v-if="step > 1" 
            variant="text" 
            @click="previousStep"
            class="nav-btn"
          >
            <v-icon left>mdi-chevron-left</v-icon>
            Previous
          </v-btn>
        </div>
        
        <div class="actions-right">
          <v-btn 
            variant="text" 
            @click="closeDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          
          <v-btn 
            v-if="step < 3" 
            color="primary" 
            @click="nextStep"
            :disabled="!canProceedToNext"
            class="nav-btn"
          >
            Next
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
          
          <v-btn 
            v-if="step === 3" 
            color="success" 
            @click="createChallenge"
            :loading="creating"
            class="create-btn"
          >
            <v-icon left>mdi-trophy</v-icon>
            Create Challenge
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  currentWeek: {
    type: Number,
    required: true
  },
  currentYear: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'challengeCreated'])

const { proxy } = getCurrentInstance()

// Reactive state
const dialog = ref(props.modelValue)
const step = ref(1)
const fileInput = ref(null)
const imageUploading = ref(false)
const tracksLoading = ref(false)
const creating = ref(false)

// Form data
const challengeForm = ref({
  imageFile: null,
  imageUrl: '',
  imageBy: '',
  raceID: null,
  car: null,
  carClass: null,
  powerups: null,
  copsRammed: '',
  operatorCopsRammed: '',
  copsDisabled: '',
  operatorCopsDisabled: '',
  topSpeed: '',
  operatorTopSpeed: '',
  costToState: '',
  operatorCostToState: '',
  durationMin: '',
  durationSec: '',
  operatorDuration: '',
  reward: '',
  rewardType: null,
  difficulty: 2  // Default to Medium
})

// Options data
const tracks = ref([])
const cars = ref([])

const operatorOptions = [
  { title: 'Greater than (+)', value: '0' },
  { title: 'Less than (-)', value: '1' },
  { title: 'Equal to (=)', value: '2' }
]

const powerupOptions = [
  { title: 'Allowed', value: 'false' },
  { title: 'Disabled', value: 'true' }
]

const classOptions = [
  { title: 'Class E', value: '872416321' },
  { title: 'Class D', value: '415909161' },
  { title: 'Class C', value: '1866825865' },
  { title: 'Class B', value: '-406473455' },
  { title: 'Class A', value: '-405837480' },
  { title: 'Class S', value: '-2142411446' },
  { title: 'Class S1', value: '86241155' },
  { title: 'Class S2', value: '221915816' },
  { title: 'Class X', value: '-543091316' }
]

const rewardTypeOptions = [
  { title: 'Cash ($)', value: '0' },
  { title: 'Speedboost (SB)', value: '1' }
]

const difficultyOptions = [
  { title: 'Very Easy', value: 0, color: '#4CAF50' },
  { title: 'Easy', value: 1, color: '#8BC34A' },
  { title: 'Medium', value: 2, color: '#FF9800' },
  { title: 'Hard', value: 3, color: '#F44336' },
  { title: 'Very Hard', value: 4, color: '#9C27B0' }
]

// Computed properties
const canProceedToNext = computed(() => {
  switch (step.value) {
    case 1:
      return challengeForm.value.imageUrl && challengeForm.value.imageBy
    case 2:
      return challengeForm.value.raceID
    case 3:
      return challengeForm.value.reward && challengeForm.value.rewardType && 
             (challengeForm.value.difficulty !== null && challengeForm.value.difficulty !== undefined)
    default:
      return false
  }
})

// Watch dialog changes
watch(() => props.modelValue, (newVal) => {
  dialog.value = newVal
  if (newVal) {
    loadTracks()
    loadCars()
  }
})

watch(dialog, (newVal) => {
  emit('update:modelValue', newVal)
})

// Methods
async function loadTracks() {
  tracksLoading.value = true
  try {
    const response = await proxy.$api({
      serviceName: "challengesmanager",
      methodName: "GetTrackList",
      parameters: [props.currentWeek]
    })
    
    if (Array.isArray(response)) {
      const allTracks = []
      response.forEach(category => {
        if (category.loop && Array.isArray(category.loop)) {
          category.loop.forEach(track => {
            const id = track.id || track.Id || track.ID
            const name = track.name || track.Name || track.DisplayName
            
            if (id && name) {
              allTracks.push({
                id: id,
                name: `${name.trim()} (${category.type})`
              })
            }
          })
        }
      })
      tracks.value = allTracks
    } else {
      tracks.value = []
    }
  } catch (error) {
    console.error('Error loading tracks:', error)
    tracks.value = []
  } finally {
    tracksLoading.value = false
  }
}

async function loadCars() {
  try {
    const response = await proxy.$api({
      serviceName: "challengesmanager",
      methodName: "GetCarsList",
      parameters: []
    })
    
    if (Array.isArray(response)) {
      const allCars = []
      response.forEach(manufacturer => {
        if (manufacturer.loop && Array.isArray(manufacturer.loop)) {
          manufacturer.loop.forEach(car => {
            const id = car.store_name || car.store_Name || car.Store_Name
            const name = car.model || car.Model
            const brand = manufacturer.marque || manufacturer.Marque
            
            if (id && name && brand) {
              allCars.push({
                id: id,
                name: `${brand} ${name}`
              })
            }
          })
        }
      })
      cars.value = allCars
    } else {
      cars.value = []
    }
  } catch (error) {
    console.error('Error loading cars:', error)
    cars.value = []
  }
}

function handleFileSelect(event) {
  const file = event.target.files[0]
  if (file) {
    challengeForm.value.imageFile = file
    uploadImage()
  }
}

async function uploadImage() {
  if (!challengeForm.value.imageFile) return
  
  imageUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', challengeForm.value.imageFile)
    formData.append('type', 'challenge')
    formData.append('easharpptr-u', localStorage.getItem('easharpptr_u'))
    formData.append('easharpptr-p', localStorage.getItem('easharpptr_p'))
    
    const response = await fetch('https://api.nightriderz.world/upload.php', {
      method: 'POST',
      body: formData
    })
    
    if (response.ok) {
      const result = await response.text()
      console.log('Upload response:', result)
      if (result !== '0' && result.startsWith('https://')) {
        challengeForm.value.imageUrl = result
        console.log('Image uploaded:', result)
      } else {
        console.error('Upload failed: Server returned:', result)
      }
    }
  } catch (error) {
    console.error('Upload error:', error)
  } finally {
    imageUploading.value = false
  }
}

function nextStep() {
  if (canProceedToNext.value && step.value < 3) {
    step.value++
  }
}

function previousStep() {
  if (step.value > 1) {
    step.value--
  }
}

function closeDialog() {
  dialog.value = false
  resetForm()
}

function resetForm() {
  step.value = 1
  challengeForm.value = {
    imageFile: null,
    imageUrl: '',
    imageBy: '',
    raceID: null,
    car: null,
    carClass: null,
    powerups: null,
    copsRammed: '',
    operatorCopsRammed: '',
    copsDisabled: '',
    operatorCopsDisabled: '',
    topSpeed: '',
    operatorTopSpeed: '',
    costToState: '',
    operatorCostToState: '',
    durationMin: '',
    durationSec: '',
    operatorDuration: '',
    reward: '',
    rewardType: null
  }
}

async function createChallenge() {
  creating.value = true
  try {
    const duration = (parseInt(challengeForm.value.durationMin) || 0) * 60 * 1000 + 
                    (parseInt(challengeForm.value.durationSec) || 0) * 1000

    const parameters = [
      props.currentYear,
      props.currentWeek,
      challengeForm.value.rewardType || '',
      challengeForm.value.raceID || '',
      challengeForm.value.imageBy || '',
      challengeForm.value.imageUrl ? challengeForm.value.imageUrl.split('/').pop() : '',
      challengeForm.value.copsRammed || '',
      challengeForm.value.operatorCopsRammed || '',
      challengeForm.value.copsDisabled || '',
      challengeForm.value.operatorCopsDisabled || '',
      challengeForm.value.costToState || '',
      challengeForm.value.operatorCostToState || '',
      duration,
      challengeForm.value.operatorDuration || '',
      challengeForm.value.powerups || '',
      challengeForm.value.carClass || '',
      challengeForm.value.car || '',
      challengeForm.value.reward || '',
      challengeForm.value.rewardType || '',
      challengeForm.value.topSpeed || '',
      challengeForm.value.difficulty !== null ? challengeForm.value.difficulty : 2  // Add difficulty parameter
    ]

    const response = await proxy.$api({
      serviceName: "challengesmanager",
      methodName: "CreateChallenge",
      parameters: parameters
    })

    if (response) {
      emit('challengeCreated', response)
      closeDialog()
      console.log('Challenge created successfully:', response)
    }
  } catch (error) {
    console.error('Error creating challenge:', error)
  } finally {
    creating.value = false
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(value)
}
</script>

<style scoped>
/* Challenge Creator - Simple Challenge Card Style */
.challenge-creator {
  background: #11141c;
  color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-height: 900px;
}

/* Header */
.creator-header {
  background: #1f2430;
  padding: 20px 24px;
  border-bottom: 2px solid #ffd700;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-icon {
  font-size: 32px;
  color: #ffd700;
  margin-right: 16px;
}

.header-text h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
}

.header-text p {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.close-btn {
  color: rgba(255, 255, 255, 0.7);
}

.close-btn:hover {
  color: #ffd700;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #1f2430;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.step {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
}

.step.active {
  color: #ffd700;
}

.step.completed {
  color: #4caf50;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.step.active .step-number {
  background: #ffd700;
  color: #11141c;
  border-color: #ffd700;
}

.step.completed .step-number {
  background: #4caf50;
  color: white;
  border-color: #4caf50;
}

.step-line {
  width: 80px;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 16px;
}

.step-line.completed {
  background: linear-gradient(90deg, #4caf50 0%, #ffd700 100%);
}

/* Content */
.creator-content {
  padding: 32px 40px;
  flex: 1;
  overflow-y: auto;
  background: #11141c;
}

.step-content {
  max-width: 700px;
  margin: 0 auto;
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 215, 0, 0.3);
}

.section-icon {
  font-size: 24px;
  color: #ffd700;
  margin-right: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
}

/* Image Upload */
.image-upload-section {
  margin-bottom: 32px;
}

.upload-area {
  border: 2px dashed rgba(255, 215, 0, 0.5);
  border-radius: 8px;
  padding: 48px 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);
}

.upload-area:hover {
  border-color: #ffd700;
  background: rgba(255, 215, 0, 0.05);
}

.upload-icon {
  color: #ffd700;
  margin-bottom: 16px;
}

.upload-area h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.upload-area p {
  margin: 0 0 24px 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.upload-btn {
  border-color: #ffd700;
  color: #ffd700;
  font-weight: 600;
}

.upload-btn:hover {
  background: #ffd700;
  color: #11141c;
}

.image-preview {
  border-radius: 8px;
  overflow: hidden;
  background: #1f2430;
  border: 1px solid rgba(255, 215, 0, 0.3);
  position: relative;
}

.image-preview img {
  width: 100%;
  height: 280px;
  object-fit: cover;
  display: block;
}

.image-actions {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 10;
}

/* Form Elements */
.form-group {
  margin-bottom: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  text-transform: uppercase;
}

.required-label::after {
  content: ' *';
  color: #ff5252;
}

.required-badge {
  background: #ff5252;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  margin-left: auto;
}

/* Condition Groups - Challenge Card Style */
.condition-group {
  background: #1f2430;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.condition-group:last-child {
  margin-bottom: 0;
}

.condition-group:hover {
  border-color: rgba(255, 215, 0, 0.3);
}

.condition-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.condition-icon {
  color: #ffd700;
  margin-right: 12px;
  font-size: 20px;
}

.condition-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
  flex: 1;
  text-transform: uppercase;
}

/* Performance Items */
.performance-grid {
  display: grid;
  gap: 16px;
}

.performance-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #11141c;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.performance-item:hover {
  border-color: rgba(255, 215, 0, 0.3);
}

.performance-label {
  display: flex;
  align-items: center;
  min-width: 180px;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
}

.performance-icon {
  color: #ffd700;
  margin-right: 8px;
  font-size: 18px;
}

.performance-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

.duration-item .performance-inputs {
  gap: 8px;
}

.duration-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-separator {
  font-weight: 700;
  font-size: 16px;
  color: #ffd700;
}

/* Reward Section - Updated to match condition groups */
.reward-preview {
  padding: 24px;
  background: linear-gradient(135deg, #ffd700 0%, #ffab00 100%);
  border-radius: 8px;
  color: #11141c;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.reward-card {
  display: flex;
  align-items: center;
}

.reward-icon {
  font-size: 40px;
  margin-right: 20px;
  color: #11141c;
}

.reward-details h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
}

.reward-amount {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
}

/* Footer Actions */
.creator-actions {
  padding: 24px 40px;
  background: #1f2430;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  margin-top: auto;
}

.actions-left,
.actions-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.nav-btn,
.create-btn,
.cancel-btn {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 12px;
}

.create-btn {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
}

.create-btn:hover {
  transform: translateY(-1px);
}

/* Simple Select Styling - Challenge Card Style */
.creator-select,
.operator-select {
  position: relative;
}

.creator-select :deep(.vs__dropdown-toggle),
.operator-select :deep(.vs__dropdown-toggle) {
  background: #11141c;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
  min-height: 36px;
  padding: 0;
}

.creator-select :deep(.vs__dropdown-toggle):hover,
.operator-select :deep(.vs__dropdown-toggle):hover {
  border-color: #ffd700;
}

.creator-select :deep(.vs__selected),
.operator-select :deep(.vs__selected) {
  color: white;
  font-weight: 500;
  margin: 6px 0 0 8px;
  font-size: 14px;
}

.creator-select :deep(.vs__search),
.operator-select :deep(.vs__search) {
  color: white;
  margin: 6px 0 0 8px;
  font-size: 14px;
}

.creator-select :deep(.vs__search::placeholder),
.operator-select :deep(.vs__search::placeholder) {
  color: rgba(255, 255, 255, 0.5);
}

/* Center dropdown arrows and clear buttons vertically */
.creator-select :deep(.vs__actions),
.operator-select :deep(.vs__actions) {
  padding: 0 8px;
  min-height: 36px;
  height: 36px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-type-select :deep(.vs__actions) {
  padding: 0 8px;
  min-height: 40px;
  height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.creator-select :deep(.vs__open-indicator),
.operator-select :deep(.vs__open-indicator),
.reward-type-select :deep(.vs__open-indicator) {
  position: absolute !important;
  top: 50% !important;
  right: 8px !important;
  transform: translateY(-50%) !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
  display: block !important;
}

.creator-select :deep(.vs__clear),
.operator-select :deep(.vs__clear),
.reward-type-select :deep(.vs__clear) {
  position: absolute !important;
  top: 50% !important;
  right: 28px !important;
  transform: translateY(-50%) !important;
  margin: 0 !important;
  padding: 2px !important;
  line-height: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: none !important;
  background-color: transparent !important;
  cursor: pointer !important;
  z-index: 10 !important;
  width: 16px !important;
  height: 16px !important;
}

/* Hide clear button when no selection */
.creator-select :deep(.vs__dropdown-toggle:not(:has(.vs__selected))) .vs__clear,
.operator-select :deep(.vs__dropdown-toggle:not(:has(.vs__selected))) .vs__clear,
.reward-type-select :deep(.vs__dropdown-toggle:not(:has(.vs__selected))) .vs__clear {
  display: none !important;
}

.creator-select :deep(.vs__dropdown-menu),
.operator-select :deep(.vs__dropdown-menu) {
  background: #1f2430;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  padding: 4px 0;
}

.creator-select :deep(.vs__dropdown-option),
.operator-select :deep(.vs__dropdown-option) {
  color: white;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
}

.creator-select :deep(.vs__dropdown-option:hover),
.operator-select :deep(.vs__dropdown-option:hover) {
  background: rgba(255, 215, 0, 0.1);
}

.creator-select :deep(.vs__dropdown-option--highlight),
.operator-select :deep(.vs__dropdown-option--highlight) {
  background: #ffd700;
  color: #11141c;
  font-weight: 600;
}

.creator-select :deep(.vs__no-options),
.operator-select :deep(.vs__no-options) {
  color: rgba(255, 255, 255, 0.6);
  padding: 8px 12px;
}

/* Fix dropdown list items margin */
.creator-select :deep(li),
.operator-select :deep(li) {
  margin-left: 0 !important;
  margin: 0;
  padding: 0;
}

/* Reward Type Select - Match v-text-field height */
.reward-type-select :deep(.vs__dropdown-toggle) {
  min-height: 40px !important;
  height: 40px !important;
}

.operator-select {
  min-width: 80px;
}

.value-input,
.duration-input {
  min-width: 100px;
}

/* Value input alignment fix */
.value-input :deep(.v-field),
.duration-input :deep(.v-field) {
  background: #11141c !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 4px !important;
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  display: flex !important;
  align-items: center !important;
  box-sizing: border-box !important;
}

.value-input :deep(.v-field__field),
.duration-input :deep(.v-field__field) {
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  display: flex !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
}

.value-input :deep(.v-field__input),
.duration-input :deep(.v-field__input) {
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  line-height: 36px !important;
  display: block !important;
  padding: 0 12px !important;
  margin: 0 !important;
  color: white !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  box-sizing: border-box !important;
}

.value-input :deep(.v-field__overlay),
.duration-input :deep(.v-field__overlay) {
  height: 36px !important;
  min-height: 36px !important;
}

.value-input :deep(.v-field__outline),
.duration-input :deep(.v-field__outline) {
  height: 36px !important;
  min-height: 36px !important;
}

.value-input :deep(.v-field__outline__start),
.duration-input :deep(.v-field__outline__start),
.value-input :deep(.v-field__outline__end),
.duration-input :deep(.v-field__outline__end) {
  height: 36px !important;
}

/* Force same height as dropdowns */
.performance-inputs .value-input,
.performance-inputs .duration-input {
  height: 36px !important;
}

.value-input :deep(.v-field:hover),
.duration-input :deep(.v-field:hover) {
  border-color: #ffd700;
}

.value-input :deep(.v-field--focused),
.duration-input :deep(.v-field--focused) {
  border-color: #ffd700;
}

.value-input :deep(.v-field__input::placeholder),
.duration-input :deep(.v-field__input::placeholder) {
  color: rgba(255, 255, 255, 0.5);
}

/* Text Inputs */
.creator-input {
  background: #11141c;
  border-radius: 4px;
}

.creator-input :deep(.v-field) {
  background: #11141c;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.creator-input :deep(.v-field:hover) {
  border-color: #ffd700;
}

.creator-input :deep(.v-field--focused) {
  border-color: #ffd700;
}

.creator-input :deep(.v-field__input) {
  color: white;
  font-weight: 500;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .creator-header {
    padding: 16px 20px;
  }
  
  .header-text h2 {
    font-size: 20px;
  }
  
  .creator-content {
    padding: 24px 20px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .performance-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .performance-label {
    min-width: auto;
  }
  
  .performance-inputs {
    justify-content: stretch;
  }
  
  .progress-steps {
    padding: 16px;
  }
  
  .step-line {
    width: 60px;
    margin: 0 12px;
  }
  
  .creator-actions {
    padding: 20px;
    flex-direction: column;
    gap: 12px;
  }
  
  .actions-left,
  .actions-right {
    justify-content: center;
  }
}

/* Difficulty Styles */
.difficulty-preview {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.difficulty-badge {
  padding: 8px 16px;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.difficulty-icon {
  font-size: 16px;
}

.difficulty-select :deep(.vs__dropdown-toggle) {
  background: #1f2430;
  border: 2px solid #374151;
  border-radius: 6px;
  color: white;
  font-weight: 500;
}

.difficulty-select :deep(.vs__dropdown-option) {
  background: #1f2430;
  color: white;
  border-bottom: 1px solid #374151;
}

.difficulty-select :deep(.vs__dropdown-option--highlight) {
  background: #ffd700;
  color: #11141c;
}
</style>
